import { motion } from "motion/react";
import { HugeiconsIcon } from "@hugeicons/react";
import {
  TextIcon,
  Mic01Icon,
  Image01Icon,
  Video01Icon,
  TokenSquareIcon,
} from "@hugeicons/core-free-icons";
import { cn } from "@/lib/utils";

interface ModelEntry {
  name: string;
  credits: number;
  unit: string;
}

interface Category {
  label: string;
  icon: typeof TextIcon;
  color: string;
  bgColor: string;
  models: ModelEntry[];
}

const categories: Category[] = [
  {
    label: "Text",
    icon: TextIcon,
    color: "text-emerald-500",
    bgColor: "bg-emerald-500/10",
    models: [
      { name: "Gemini 3 Flash", credits: 2, unit: "per 1K tokens" },
      { name: "Gemini 3.1 Pro", credits: 3, unit: "per 1K tokens" },
      { name: "Claude Sonnet 4.6", credits: 5, unit: "per 1K tokens" },
      { name: "Grok 4.1", credits: 3, unit: "per 1K tokens" },
    ],
  },
  {
    label: "Image",
    icon: Image01Icon,
    color: "text-sky-500",
    bgColor: "bg-sky-500/10",
    models: [
      { name: "Nano Banana 2", credits: 8, unit: "per image" },
      { name: "Nano Banana Pro", credits: 25, unit: "per image" },
    ],
  },
  {
    label: "Video",
    icon: Video01Icon,
    color: "text-rose-500",
    bgColor: "bg-rose-500/10",
    models: [
      { name: "Veo 3.1 Fast", credits: 200, unit: "per 5s clip" },
      { name: "Veo 3.1 Standard", credits: 500, unit: "per 5s clip" },
      { name: "Wan 2.1", credits: 50, unit: "per 5s clip" },
    ],
  },
  {
    label: "Audio",
    icon: Mic01Icon,
    color: "text-amber-500",
    bgColor: "bg-amber-500/10",
    models: [
      { name: "OpenAI TTS", credits: 3, unit: "per 1K characters" },
      { name: "ElevenLabs", credits: 7, unit: "per 1K characters" },
      { name: "MusicGen", credits: 12, unit: "per generation" },
    ],
  },
];

export function CreditsTable() {
  return (
    <section className="border-border border-t py-20 sm:py-28">
      <div className="mx-auto max-w-6xl px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
          className="mb-12 text-center"
        >
          <p className="text-brand mb-4 text-sm font-semibold tracking-wide uppercase">
            Credits
          </p>
          <h2 className="font-display text-foreground text-3xl font-normal tracking-tight sm:text-4xl">
            Credit consumption
          </h2>
          <p className="text-muted-foreground mx-auto mt-3 max-w-md text-base">
            Each model uses a different amount of credits. Here's a breakdown by
            category.
          </p>
        </motion.div>

        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {categories.map((category, i) => (
            <motion.div
              key={category.label}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{
                duration: 0.5,
                delay: 0.1 * i + 0.15,
                ease: [0.16, 1, 0.3, 1],
              }}
              className="bg-card ring-foreground/10 flex flex-col rounded-2xl ring-1"
            >
              {/* Category header */}
              <div className="flex items-center gap-3 px-5 pt-5 pb-4">
                <div
                  className={cn(
                    "flex size-8 items-center justify-center rounded-lg",
                    category.bgColor
                  )}
                >
                  <HugeiconsIcon
                    icon={category.icon}
                    className={cn("size-4", category.color)}
                  />
                </div>
                <span className="text-foreground text-sm font-semibold">
                  {category.label}
                </span>
              </div>

              {/* Model rows */}
              <div className="border-border/50 border-t px-5 py-2">
                {category.models.map((model, modelIdx) => (
                  <div
                    key={model.name}
                    className={cn(
                      "flex items-center justify-between py-3",
                      modelIdx > 0 && "border-border/50 border-t"
                    )}
                  >
                    <div className="min-w-0">
                      <p className="text-foreground truncate text-sm font-medium">
                        {model.name}
                      </p>
                      <p className="text-muted-foreground text-xs">
                        {model.unit}
                      </p>
                    </div>
                    <div className="ml-3 flex items-center gap-1 shrink-0">
                      <span className="text-foreground text-sm font-semibold tabular-nums">
                        {model.credits}
                      </span>
                      <HugeiconsIcon
                        icon={TokenSquareIcon}
                        className="text-muted-foreground size-3.5"
                      />
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          ))}
        </div>

        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.3 }}
          className="text-muted-foreground/60 mt-5 text-center text-xs"
        >
          Credits are approximate and may vary based on input complexity and
          output length.
        </motion.p>
      </div>
    </section>
  );
}
